import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy, signal } from '@angular/core'
import { Sort } from '@angular/material/sort'
import { Router, ActivatedRoute } from '@angular/router'
import { forkJoin, of, switchMap, Subject } from 'rxjs'
import { take, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea, CycleConfigDto, ReviewPlan, UserReview, UserReviewClient } from 'src/app/shared/services/ppv.api'
import { GlobalAdminService } from 'src/app/dvm-tool/globalAdmins.service'
import dayjs from 'dayjs'
import { ToastrService } from 'ngx-toastr'
import utc from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(tz)

@Component({
   selector: 'dvm-tool-dashboard',
   templateUrl: './dvm-tool-dashboard.component.html',
   styleUrls: ['./dvm-tool-dashboard.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class DVMToolDashboardComponent implements OnInit, OnDestroy {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   public isDVMToolAdmin = false
   public isDVM = false
   public isRM = false
   public isHM = false
   public isCOS = false
   public isCOSA = false
   public isOpsVP = false
   public isOpsDirector = false
   public userId: number = null
   public userName: string
   isGlobalAdmin = this.globalAdminService.isGlobalAdmin
   isReportingAdmin = this.globalAdminService.isReportingAdmin

   reviewType: string = 'DVM'

   public userReviews: UserReview[] = []
   public reviewPlans: ReviewPlan[] = []

   public isUserReviewsLoaded = false
   public isPlansLoaded = false

   public allRows: ViewRow[] = []
   public dashboardRows: ViewRow[] = []

   public pagedRows: ViewRow[] = []
   public pageSize = 200
   public pageIndex = 0
   public totalPages = 1

   // filters (null = no filter)
   selectedPractice: string | null = null
   selectedStatus: string | null = null
   selectedReviewerName: string | null = null
   selectedRMName: string | null = null

   selectedCycle = 'Q3'
   cycleConfigs: CycleConfigDto[] = []
   cycleConfigsLoaded = false
   tabCycles = ['Q1', 'Q2', 'Q3', 'Q4']

   public cycleOpen = true
   public cycleClosedReason = ''
   public readOnlyMode = false

   public cycleTabViews: { cycle: string; enabled: boolean; open: boolean; closedReason: string }[] = []

   reviewerNameOptions: { label: string; value: string | null }[] = []
   practiceOptions: { label: string; value: string | null }[] = []
   statusOptions: { label: string; value: string | null }[] = [
      { label: 'Show All', value: null },
      { label: 'Completed', value: 'completed' },
      { label: 'In Review', value: 'in-review' },
      { label: 'In Progress', value: 'in-progress' },
      { label: 'Not Started', value: 'not-started' },
   ]

   userDeferDialog = signal<{ show: boolean; row: DashboardRow | null }>({ show: false, row: null })
   userFollowUpDialog = signal<{ show: boolean; row: DashboardRow | null }>({ show: false, row: null })
   userFollowUpDeferredOn: Date | string | null = null
   private adminUnreadCountMap = new Map<string, number>()

   rejectedDialog = signal<{ show: boolean; row: DashboardRow | null }>({ show: false, row: null })
   rejectedDialogRejectReason = ''
   rejectedDialogResolvedBy = ''
   rejectedDialogResolvedOn: Date | string | null = null

   exportDialog = signal<{ show: boolean }>({ show: false })

   private adminFollowUpMap = new Map<string, number>()
   userFollowUpMessages: {
      id: number
      deferralId: number
      message: string
      sentById: string
      sentByName: string
      isAdmin: boolean
      sentOn: Date | string
   }[] = []
   userFollowUpMessage = ''
   userFollowUpLoading = false
   userFollowUpSaving = false

   deferReasonCategory = ''
   deferCategoryOpen = false
   deferQuarter: string | null = null

   readonly deferQuarterOptions: Array<{ value: string; label: string; sub: string; endsOn: string }> = [
      { value: 'Q4 2026', label: 'Q4 2026', sub: '(Oct – Dec)', endsOn: '2026-12-31' },
      { value: 'Q1 2027', label: 'Q1 2027', sub: '(Jan – Mar)', endsOn: '2027-03-31' },
      { value: 'Q2 2027', label: 'Q2 2027', sub: '(Apr – Jun)', endsOn: '2027-06-30' },
      { value: 'Q3 2027', label: 'Q3 2027', sub: '(Jul – Sep)', endsOn: '2027-09-30' },
      { value: '1 Year', label: '1 Year', sub: '', endsOn: '' },
   ]

   readonly deferCategoryOptions = [
      {
         label: 'Newly hired employee',
         description: 'The team member has not been in the role long enough (e.g., less than 60–90 days) to complete a meaningful annual review.',
      },
      {
         label: 'Recently completed review or promotion',
         description: 'The employee recently underwent a performance review tied to a promotion, transfer, role change, or compensation adjustment.',
      },
      {
         label: 'Extended leave of absence',
         description: 'The employee was on extended medical, parental, military, or personal leave for a significant portion of the review period.',
      },
      {
         label: 'Insufficient time under current manager',
         description:
            'The employee changed departments, positions, or reporting structures during the review cycle, making a standard review less applicable.',
      },
      {
         label: 'Performance improvement or corrective action process already in progress',
         description: 'The employee is currently on a formal coaching or performance improvement plan with separate documented evaluation timelines.',
      },
      {
         label: 'Separation from the organization',
         description: 'The employee has submitted resignation notice, has a planned termination date, or is otherwise exiting the organization.',
      },
      { label: 'Relief or Seasonal', description: "The individual may not fall within the organization's standard annual review process." },
      { label: 'Other', description: '' },
   ]

   get canSelectDeferQuarter(): boolean {
      if (
         this.deferReasonCategory == this.deferCategoryOptions[1].label ||
         this.deferReasonCategory == this.deferCategoryOptions[2].label ||
         this.deferReasonCategory == this.deferCategoryOptions[4].label ||
         this.deferReasonCategory == this.deferCategoryOptions[5].label ||
         this.deferReasonCategory == this.deferCategoryOptions[6].label
      ) {
         return true
      } else if (
         this.deferReasonCategory == 'Newly hired employee' &&
         dayjs(this.userDeferDialog()?.row.hireDate).isAfter(dayjs().subtract(9, 'month'))
      ) {
         return true
      }
      return false
   }

   get needsAdminApproval(): boolean {
      if (!this.canSelectDeferQuarter && this.deferReasonCategory == 'Newly hired employee') {
         return true
      } else if (this.deferReasonCategory == this.deferCategoryOptions[3].label) {
         return true
      } else if (!this.canSelectDeferQuarter && this.deferReasonCategory == 'Relief or Seasonal') {
         return true
      } else if (this.deferReasonCategory == 'Other') {
         return true
      }
      return false
   }

   get isAutoApprovedDeferral(): boolean {
      return !!this.deferQuarter
   }

   selectDeferQuarter(value: string | null): void {
      if (value && !this.canSelectDeferQuarter) return
      this.deferQuarter = value
   }

   showAllNames = false
   deferredFilterOptions: { label: string; value: string | null; disabled?: boolean }[] = [
      { label: 'Show All', value: null },
      { label: 'Active', value: 'active' },
      { label: 'Deferred', value: 'deferred' },
      { label: 'On Leave', value: 'LOAINACTIVE' },
      { label: 'More...', value: '__more__' },
   ]
   selectedDeferredFilters: string[] = ['null']
   deferReason = ''
   isSubmittingUpdate = false
   searchName: string = ''

   private dvmNames: string[] = []
   rmOptions: { label: string; value: string | null }[] = []

   private isFetching = false

   private destroy$ = new Subject<void>()
   private searchInput$ = new Subject<string>()

   trackByReviewee = (_: number, row: DashboardRow) => row.revieweeId

   private buildFilterOptions() {
      const byFilters = this.selectedDeferredFilters
      const byName = this.searchName?.toLowerCase()

      const matchesOtherFilters = (r: ViewRow, exclude: 'practice' | 'reviewer' | 'rm' | 'status') =>
         (byName ? r._nameLower?.includes(byName) : true) &&
         (byFilters.length === 0
            ? true
            : byFilters.some((f) =>
                 f === 'active'
                    ? !this.isDeferredRow(r) && r.revieweeUserStatus != 'LOAINACTIVE'
                    : f === 'deferred'
                      ? this.isDeferredRow(r)
                      : f === 'LOAINACTIVE'
                        ? r.revieweeUserStatus == 'LOAINACTIVE'
                        : true
              )) &&
         (exclude === 'reviewer' || !this.selectedReviewerName ? true : this.getReviewerName(r) === this.selectedReviewerName) &&
         (exclude === 'practice' || !this.selectedPractice ? true : r.practice === this.selectedPractice) &&
         (exclude === 'rm' || !this.selectedRMName ? true : r.rmName === this.selectedRMName) &&
         (exclude === 'status' || !this.selectedStatus ? true : this.getReviewStatus(r) === this.selectedStatus)

      const reviewers = new Set<string>()
      const practices = new Set<string>()
      const rms = new Set<string>()
      const statuses = new Set<string>()
      const names = new Set<string>()

      for (const r of this.allRows) {
         if (matchesOtherFilters(r, 'reviewer')) {
            const reviewer = this.getReviewerName(r)
            if (reviewer) reviewers.add(reviewer)
         }

         if (matchesOtherFilters(r, 'practice') && r.practice) practices.add(r.practice)
         if (matchesOtherFilters(r, 'rm') && r.rmName) rms.add(r.rmName)
         if (matchesOtherFilters(r, 'status')) statuses.add(this.getReviewStatus(r))
         if (r.name) names.add(r.name)
      }

      const toOptions = (s: Set<string>) =>
         Array.from(s)
            .sort((a, b) => a.localeCompare(b))
            .map((v) => ({ label: v, value: v }))

      this.reviewerNameOptions = toOptions(reviewers)
      this.practiceOptions = toOptions(practices)
      this.rmOptions = toOptions(rms)
      this.statusOptions = [
         { label: 'Show All', value: null },
         ...(statuses.has('completed') ? [{ label: 'Completed', value: 'completed' }] : []),
         ...(statuses.has('in-review') ? [{ label: 'In Review', value: 'in-review' }] : []),
         ...(statuses.has('in-progress') ? [{ label: 'In Progress', value: 'in-progress' }] : []),
         ...(statuses.has('not-started') ? [{ label: 'Not Started', value: 'not-started' }] : []),
      ]
      this.dvmNames = Array.from(names).sort((a, b) => a.localeCompare(b))

      this.rebuildDeferredOptions()
   }

   rebuildDeferredOptions() {
      const base = [
         { label: 'Show All', value: null },
         { label: 'Active', value: 'active' },
         { label: 'Deferred', value: 'deferred' },
         { label: 'On Leave', value: 'LOAINACTIVE' },
      ]

      if (this.showAllNames) {
         const capped = this.dvmNames.slice(0, 200)
         this.deferredFilterOptions = [...base, { label: 'Hide names', value: '__less__' }, ...capped.map((v) => ({ label: v, value: `name:${v}` }))]
      } else {
         this.deferredFilterOptions = [...base, { label: 'More...', value: '__more__' }]
      }
   }

   onNameSearch(val: string): void {
      this.searchInput$.next(val ?? '')
   }

   // current sort (default none)
   private currentSort: Sort = { active: '', direction: '' }

   constructor(
      private authMgr: AuthManager,
      private router: Router,
      private route: ActivatedRoute,
      private toastr: ToastrService,
      private userReviewClient: UserReviewClient,
      public globalAdminService: GlobalAdminService
   ) {
      this.searchInput$.pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((val) => {
         this.searchName = val
         this.pageIndex = 0
         this.applyFiltersSort()
      })
   }

   ngOnInit(): void {
      this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
         if (params['rt'] !== undefined && params['rt'] !== null && params['rt'] !== '') {
            this.reviewType = params['rt']
         }
      })

      this.authMgr
         .getCurrentUser()
         .pipe(take(1), takeUntil(this.destroy$))
         .subscribe((user) => {
            this.applyUserFlags(user)

            if (!this.isDVMToolAdmin) {
               this.router.navigate(['/performance-hub/reviews', this.userId], { queryParamsHandling: 'preserve' })
               return
            }

            // Load cycle config first, then load table data
            this.loadCycleConfig(() => this.loadDashboard(this.selectedCycle))
         })
   }

   ngOnDestroy(): void {
      this.destroy$.next()
      this.destroy$.complete()
   }

   private applyUserFlags(user: any): void {
      this.userId = user.id
      this.userName = user.displayName

      this.isDVMToolAdmin = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['DVMToolAdmin'],
      })

      const cDVM = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['DVMTool'],
      })

      const cRM = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['RM'],
      })

      const cHM = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['HospitalManager'],
      })

      const cOpsVP = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['OpsVP'],
      })

      const cOpsDirector = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['OpsDirector'],
      })

      this.isRM = user.dayforceJobCode === 'OPS-RM'
      this.isHM = user.dayforceJobCode === 'HM'
      this.isCOSA = user.dayforceJobCode === 'COSA'
      this.isCOS = user.dayforceJobCode === 'VET-COS'
      this.isOpsVP = user.dayforceJobCode === 'OPS-SVP' || user.dayforceJobCode === 'OPS-VP' || user.dayforceJobCode === 'CORP-VP' || cOpsVP
      this.isOpsDirector = user.dayforceJobCode === 'CORP-DIR' || cOpsDirector
      this.isDVM = user.dayforceJobCode === 'VET' || cDVM
   }

   // Cycle config helpers
   loadCycleConfig(onLoaded?: () => void): void {
      this.userReviewClient
         .getCycleConfig()
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: (configs) => {
               this.cycleConfigs = configs ?? []
               this.cycleConfigsLoaded = true
               this.refreshCycleState()
               onLoaded?.()
            },
            error: () => {
               this.cycleConfigs = []
               this.cycleConfigsLoaded = true
               this.refreshCycleState()
               onLoaded?.()
            },
         })
   }

   private refreshCycleState(): void {
      const quarter = this.reviewType === 'DVM' ? this.selectedCycle : undefined
      this.cycleOpen = this.isCycleOpen(this.reviewType, quarter)
      this.cycleClosedReason = this.getCycleClosedReason(this.reviewType, quarter)
      //this.readOnlyMode = !this.cycleOpen && !this.isReportingAdmin()

      this.cycleTabViews = this.tabCycles.map((c) => ({
         cycle: c,
         enabled: this.isCycleEnabled('DVM', c),
         open: this.isCycleOpen('DVM', c),
         closedReason: this.isCycleOpen('DVM', c) ? '' : this.getCycleClosedReason('DVM', c),
      }))
   }

   private getCycleConfig(reviewType: string, quarter?: string): CycleConfigDto | null {
      let key: string
      if (reviewType === 'DVM' && quarter) {
         key = `DVM_${quarter}` // e.g. "DVM_Q1"
      } else {
         key = reviewType // e.g. "HM", "COS", "RM", "DO"
      }
      return this.cycleConfigs.find((c) => c.key === key) ?? null
   }

   isCycleOpen(reviewType: string, quarter?: string): boolean {
      if (!this.cycleConfigsLoaded) return true

      const config = this.getCycleConfig(reviewType, quarter)

      // No config row = not configured = treat as closed
      if (!config) return false

      if (!config.isEnabled) return false

      const now = dayjs()
      if (config.openDate) {
         const open = dayjs(config.openDate).startOf('day')
         if (now.isBefore(open)) return false
      }
      if (config.closeDate) {
         const close = dayjs(config.closeDate).endOf('day')
         if (now.isAfter(close)) return false
      }

      return true
   }

   isCycleEnabled(reviewType: string, quarter?: string): boolean {
      const config = this.getCycleConfig(reviewType, quarter)
      if (!config || !config.isEnabled) return false
      return true
   }

   getCycleClosedReason(reviewType: string, quarter?: string): string {
      if (!this.cycleConfigsLoaded) return ''

      const config = this.getCycleConfig(reviewType, quarter)

      if (!config)
         return 'This review cycle has been disabled or is outside its active date window. Reviews cannot be opened until the cycle is re-enabled.'

      if (!config.isEnabled)
         return 'This review cycle has been disabled or is outside its active date window. Reviews cannot be opened until the cycle is re-enabled.'

      const now = dayjs()
      if (config.openDate && now.isBefore(dayjs(config.openDate).startOf('day'))) {
         return `This review cycle opens on ${dayjs(config.openDate).format('MMM D, YYYY')}.`
      }
      if (config.closeDate && now.isAfter(dayjs(config.closeDate).endOf('day'))) {
         return `This review cycle closed on ${dayjs(config.closeDate).format('MMM D, YYYY')}. Reviews cannot be opened until the cycle is re-enabled.`
      }

      return 'This cycle is currently unavailable.'
   }

   get isCurrentCycleOpen(): boolean {
      return this.cycleOpen
   }

   get currentCycleClosedReason(): string {
      return this.cycleClosedReason
   }

   onRowClick(row: DashboardRow): void {
      //if (row.revieweeUserStatus == 'LOAINACTIVE' && !this.isReportingAdmin()) return

      // Block navigation if the cycle is closed
      //if (!this.cycleOpen && !this.isReportingAdmin()) return

      if (row) {
         const localDate = dayjs.utc(row.lastUpdated).local()
         const formattedDate = localDate.format('MM/DD/YYYY')
         const formattedTime = localDate.format('hh:mm A')
         const lastUpdated = formattedDate + ' ' + formattedTime
         this.router.navigate(['/performance-hub/reviews', row.revieweeId], {
            queryParams: {
               rt: this.reviewType == 'OPEN' ? row.reviewType : this.reviewType,
               from: this.reviewType,
            },
            state: {
               lastUpdated: lastUpdated,
            },
         })
      }
   }

   // --- Labels ---
   getRevieweeLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'HM':
            return short ? 'HM' : 'Hospital Manager'
         case 'COS':
            return short ? 'COS' : 'Chief of Staff'
         case 'RM':
            return short ? 'RM' : 'Regional Manager'
         case 'DO':
            return short ? 'DO' : 'Director of Operations'
         case 'OPEN':
            return short ? 'RM' : 'Regional Manager'
         case 'DVM':
         default:
            return short ? 'aDVM' : 'Associate DVM'
      }
   }

   getReviewerLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'HM':
            return short ? 'RM' : 'Regional Manager'
         case 'COS':
            return short ? 'COSA' : 'Chief of Staff Advisor'
         case 'RM':
            return short ? 'DO' : 'Director of Operations'
         case 'DO':
            return short ? 'VP' : 'Vice President of Operations'
         case 'OPEN':
            return short ? 'RM' : 'Regional Manager'
         case 'DVM':
         default:
            return short ? 'COS' : 'Chief of Staff'
      }
   }

   getReviewerName(userReview: any): string {
      switch (this.reviewType) {
         case 'HM':
            return userReview.rmName
         case 'COS':
            return userReview.vetLeaderName
         case 'RM':
            return userReview.directorName
         case 'DO':
            return userReview.vpName
         case 'OPEN':
            return userReview.rmName
         case 'DVM':
         default:
            return userReview.cosName
      }
   }

   getReviewerId(userReview: any): string {
      switch (this.reviewType) {
         case 'HM':
            return userReview.rmUserId
         case 'COS':
            return userReview.vetLeaderId
         case 'RM':
            return userReview.directorId
         case 'DO':
            return userReview.vpId
         case 'DVM':
         default:
            return userReview.cosUserId
      }
   }

   private loadDashboard(cycle?: string): void {
      if (this.isFetching) return
      this.isFetching = true
      this.isUserReviewsLoaded = false
      this.isPlansLoaded = false

      // OPEN spans all review types, so it has its own endpoint and no plans call.
      if (this.reviewType == 'OPEN' || this.selectedCycle === 'OPEN') {
         this.userReviewClient
            .getOpenCycleReviews()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
               next: (reviews) => {
                  this.userReviews = reviews
                  this.reviewPlans = []
                  this.isUserReviewsLoaded = true
                  this.isPlansLoaded = true
                  this.isFetching = false

                  this.updateDashboardRows()
                  this.buildFilterOptions()
                  this.currentSort = { active: 'name', direction: 'asc' }
                  this.applyFiltersSort()

                  this.loadAdminFollowUpFlags()
               },
               error: (error) => {
                  console.error('Error fetching data:', error)
                  this.isUserReviewsLoaded = true
                  this.isPlansLoaded = true
                  this.isFetching = false
               },
            })
         return
      }

      forkJoin({
         reviews: this.userReviewClient.getUserReviewsAll(this.reviewType, cycle),
         plans: this.userReviewClient.getUsersAll(this.reviewType, cycle),
      })
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: ({ reviews, plans }) => {
               this.userReviews = reviews
               this.reviewPlans = plans
               this.isUserReviewsLoaded = true
               this.isPlansLoaded = true
               this.isFetching = false

               this.updateDashboardRows()
               this.buildFilterOptions()
               this.applyFiltersSort()

               this.loadAdminFollowUpFlags()
            },
            error: (error) => {
               console.error('Error fetching data:', error)
               this.isUserReviewsLoaded = true
               this.isPlansLoaded = true
               this.isFetching = false
            },
         })
   }

   // Kept for API compatibility with anything that calls these.
   // Both now trigger the same single coordinated reload.
   public getUserReviewsAll(cycle?: string) {
      this.loadDashboard(cycle ?? this.selectedCycle)
   }

   public getUsersAll(cycle?: string) {
      this.loadDashboard(cycle ?? this.selectedCycle)
   }

   private loadAdminFollowUpFlags(): void {
      this.userReviewClient
         .getFollowUpSummary()
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: (summary) => {
               this.adminFollowUpMap.clear()
               this.adminUnreadCountMap.clear()

               for (const s of summary ?? []) {
                  const id = s.revieweeId?.toString()
                  if (!id) continue
                  this.adminFollowUpMap.set(id, 1)
                  this.adminUnreadCountMap.set(id, s.unreadCount ?? 0)
               }

               for (const row of this.allRows) {
                  const id = row.revieweeId?.toString()
                  row.hasAdminFollowUp = this.adminFollowUpMap.has(id)
                  row._unread = this.adminUnreadCountMap.get(id) ?? 0
               }
            },
            error: () => {
               /* non-critical, follow-up button just won't show */
            },
         })
   }

   private decorate(rows: DashboardRow[]): ViewRow[] {
      const reviewerLabel = this.getReviewerLabel(true)
      const out: ViewRow[] = []
      for (const r of rows) {
         const v = r as ViewRow
         const id = r.revieweeId?.toString()

         v._deferred = this.isDeferredRow(r)
         v._onLeave = r.revieweeUserStatus === 'LOAINACTIVE'
         v._unread = this.adminFollowUpMap.has(id) ? (this.adminUnreadCountMap.get(id) ?? 0) : 0
         v._status = this.getReviewStatus(r)
         v._nameLower = (r.name ?? '').toLowerCase()
         v._reviewerName = this.getReviewerName(r)
         v._newReviewerName = (r as any)['new' + reviewerLabel] ?? ''
         v._dvmIncomplete = this.isIncompleteSubmitted(r.isDVMSubmitted, r.dvmSubmittedOn)
         v._cosIncomplete = this.isIncompleteSubmitted(r.isCOSSubmitted, r.cosSubmittedOn)

         out.push(v)
      }
      return out
   }

   updateDashboardRows() {
      const planMap = new Map<string, ReviewPlan>()
      this.reviewPlans.forEach((plan) => {
         planMap.set(plan.revieweeUserId?.toString(), plan)
      })

      const sortedReviews = this.userReviews
         .map((ur) => ({ ur, ts: ur.dateUpdated ? Date.parse(ur.dateUpdated as any) || 0 : 0 }))
         .sort((a, b) => b.ts - a.ts)
         .map((x) => x.ur)

      const dashboardRowsFromExisting = sortedReviews.map((plan) => {
         const reviewPlan = planMap.get(plan.revieweeId?.toString())

         const upd = plan.dateUpdated ? dayjs.utc(plan.dateUpdated).local() : null
         const updDate = upd ? upd.format('MM/DD/YYYY') : ''
         const updTime = upd ? upd.format('hh:mm A') : ''
         const updFull = upd ? updDate + ' ' + updTime : ''

         return {
            name: reviewPlan?.revieweeDisplayName || plan.revieweeName || '',
            practice: reviewPlan?.practiceName || plan.practiceName || '',
            revieweeUserStatus: reviewPlan?.revieweeUserStatus,
            rmName: reviewPlan?.rmDisplayName || plan.rmName || '',
            vetLeaderName: reviewPlan?.vetLeaderDisplayName || plan.vetLeaderName || '',
            cosName: reviewPlan?.cosDisplayName || plan.cosName || '',
            directorName: reviewPlan?.directorDisplayName || plan.directorName || '',
            vpName: reviewPlan?.vpDisplayName || plan.vpName || '',
            hmName: reviewPlan?.hmDisplayName || plan.hmName || '',
            hmName2: reviewPlan?.hmDisplayName2 || plan.hmName2 || '',
            lastUpdated: updFull,
            lastUpdatedDisplay: updDate,
            hireDate: reviewPlan?.hireDate || plan.dateHired ? dayjs(reviewPlan?.hireDate || plan.dateHired).format('MM/DD/YYYY') : '',
            reviewUpdatedDate: updFull,
            revieweeId: plan.revieweeId,
            isCompleted: (plan as any).isCompleted ?? null,
            isDVMSubmitted: (plan as any).isDVMSubmitted ?? null,
            isCOSSubmitted: (plan as any).isCOSSubmitted ?? null,
            dvmSubmittedOn: (plan as any).dvmSubmittedOn
               ? dayjs
                    .utc((plan as any).dvmSubmittedOn)
                    .local()
                    .toDate()
                    .toLocaleDateString()
               : '',
            cosSubmittedOn: (plan as any).cosSubmittedOn
               ? dayjs
                    .utc((plan as any).cosSubmittedOn)
                    .local()
                    .toDate()
                    .toLocaleDateString()
               : '',
            completedOn: (plan as any).completedOn
               ? dayjs
                    .utc((plan as any).completedOn)
                    .local()
                    .toDate()
                    .toLocaleDateString()
               : '',
            isDeferred: (plan.isDeferred || reviewPlan?.isDeferred) ?? false,
            deferReason: (plan.deferReason || reviewPlan?.deferReason) ?? null,
            deferralStatus: (plan.deferralStatus || reviewPlan?.deferralStatus) ?? null,
            whenCompletedBy: (plan.whenCompletedBy || reviewPlan?.whenCompletedBy) ?? null,
            whenOpenCompletedBy: (plan.whenOpenCompletedBy || reviewPlan?.whenOpenCompletedBy) ?? null,
            practiceSKey: reviewPlan?.practiceSKey || plan.practiceSKey || 0,
            reviewType: (plan as any).reviewType || '',
            newRM: reviewPlan?.rmDisplayName == plan?.rmName ? '' : plan?.rmName,
            newCOSA: reviewPlan?.vetLeaderDisplayName == plan?.vetLeaderName ? '' : plan?.vetLeaderName,
            newCOS: reviewPlan?.cosDisplayName == plan?.cosName ? '' : plan?.cosName,
            newDO: reviewPlan?.directorDisplayName == plan?.directorName ? '' : plan?.directorName,
            newVP: reviewPlan?.vpDisplayName == plan?.vpName ? '' : plan?.vpName,
            newHM: reviewPlan?.hmDisplayName == plan?.hmName ? '' : plan?.hmName,
            hasStarted: (plan as any).hasStarted ?? null,
            hasAttachment: (plan as any).hasAttachment ?? null,
         }
      })

      const reviewedIds = new Set<string>(this.userReviews.map((ur) => ur.revieweeId?.toString()))
      const unmatchedDvms: ReviewPlan[] = this.reviewPlans.filter((rp) => !reviewedIds.has(rp.revieweeUserId?.toString()))

      const dashboardRowsForNew: DashboardRow[] = unmatchedDvms.map((reviewPlan) => ({
         name: reviewPlan.revieweeDisplayName || '',
         practice: reviewPlan.practiceName || '',
         revieweeUserStatus: reviewPlan.revieweeUserStatus || '',
         lastUpdated: 'INCOMPLETE',
         lastUpdatedDisplay: 'INCOMPLETE',
         hireDate: reviewPlan.hireDate ? dayjs(reviewPlan.hireDate).format('MM/DD/YYYY') : '',
         rmName: reviewPlan.rmDisplayName || '',
         vetLeaderName: reviewPlan.vetLeaderDisplayName || '',
         cosName: reviewPlan.cosDisplayName || '',
         directorName: reviewPlan.directorDisplayName || '',
         vpName: reviewPlan.vpDisplayName || '',
         hmName: reviewPlan.hmDisplayName || '',
         hmName2: reviewPlan.hmDisplayName2 || '',
         revieweeId: reviewPlan.revieweeUserId,
         isCompleted: false,
         isDVMSubmitted: false,
         isCOSSubmitted: false,
         dvmSubmittedOn: '',
         cosSubmittedOn: '',
         completedOn: '',
         isDeferred: reviewPlan.isDeferred,
         deferReason: reviewPlan.deferReason,
         deferralStatus: reviewPlan.deferralStatus,
         whenCompletedBy: reviewPlan.whenCompletedBy,
         whenOpenCompletedBy: reviewPlan.whenOpenCompletedBy,
         practiceSKey: reviewPlan.practiceSKey || 0,
         reviewType: '',
         hasStarted: false,
         hasAttachment: false,
      }))

      const combinedDashboards = [...dashboardRowsForNew, ...dashboardRowsFromExisting]
      const uniqueRowsMap = new Map<string, DashboardRow>()
      combinedDashboards.forEach((row) => {
         const key = row.revieweeId?.toString()
         if (!uniqueRowsMap.has(key)) uniqueRowsMap.set(key, row)
      })

      const uniqueDashboardRowsFromExisting = Array.from(uniqueRowsMap.values())
      const filteredRows =
         this.reviewType === 'RM' && this.isRM
            ? uniqueDashboardRowsFromExisting.filter((row) => row.revieweeId?.toString() === this.userId?.toString())
            : uniqueDashboardRowsFromExisting

      this.allRows = this.decorate(filteredRows)
      this.dashboardRows = this.allRows
      this.repage()
   }

   isIncompleteSubmitted(isSubmitted: boolean | null | undefined, submittedOn: any): boolean {
      if (isSubmitted === false) return true
      if (submittedOn == null) return true
      if (typeof submittedOn === 'string' && submittedOn.trim().toLowerCase() === 'null') return true
      if (typeof submittedOn === 'string' && submittedOn.trim() === '') return true
      return !this.isValidDate(submittedOn)
   }

   exportColumns: Array<{ key: string; label: string; icon: string; selected: boolean }> = []

   openExportModal(): void {
      this.exportColumns = [
         { key: 'name', label: this.getRevieweeLabel(false), icon: 'fa-user', selected: true },
         { key: 'practice', label: 'Hospital Name', icon: 'fa-house-medical', selected: true },
         { key: 'hireDate', label: 'Hire Date', icon: 'fa-calendar-day', selected: true },
         { key: 'reviewerName', label: this.getReviewerLabel(false), icon: 'fa-user-tie', selected: true },
         { key: 'dvmSubmittedOn', label: `${this.getRevieweeLabel(true)} Submitted`, icon: 'fa-paper-plane', selected: true },
         { key: 'cosSubmittedOn', label: `${this.getReviewerLabel(true)} Submitted`, icon: 'fa-user-check', selected: true },
         { key: 'status', label: 'Submission Status', icon: 'fa-circle-check', selected: true },
         { key: 'isDeferred', label: 'User Deferred?', icon: 'fa-hourglass-half', selected: true },
         { key: 'revieweeUserStatus', label: 'Dayforce Status', icon: 'fa-user-gear', selected: false },
      ]
      this.exportDialog.set({ show: true })
   }

   closeExportModal(): void {
      this.exportDialog.set({ show: false })
   }

   get selectedExportCount(): number {
      return this.exportColumns.filter((c) => c.selected).length
   }

   setAllExportColumns(selected: boolean): void {
      this.exportColumns.forEach((c) => (c.selected = selected))
   }

   exportDashboardRowsXlsx(): void {
      const rows: DashboardRow[] = [...(this.dashboardRows ?? [])]
         .filter((r) => r.revieweeUserStatus != 'LOAINACTIVE')
         .sort(
            (a, b) =>
               (a.reviewType ?? '').localeCompare(b.reviewType ?? '') ||
               (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
         )
      const cols = this.exportColumns.filter((c) => c.selected)
      if (!rows.length || !cols.length) return

      const colWidths: Record<string, number> = {
         name: 24,
         practice: 30,
         hireDate: 12,
         reviewerName: 22,
         dvmSubmittedOn: 17,
         cosSubmittedOn: 17,
         status: 15,
         isDeferred: 14,
         revieweeUserStatus: 15,
      }

      const statusLabels: Record<string, string> = {
         completed: 'Completed',
         'in-review': 'In Review',
         'in-progress': 'In Progress',
         'not-started': 'Not Started',
      }
      const statusFontRgb: Record<string, string> = {
         completed: '18A880',
         'in-review': '0672BA',
         'in-progress': '90278E',
         'not-started': '979797',
      }

      const aoa: any[][] = [cols.map((c) => this.xlsxHdr(c.label))]

      rows.forEach((r, i) => {
         const bg = i % 2 === 0 ? 'FFFFFF' : 'F2F8F8'
         aoa.push(
            cols.map((c) => {
               switch (c.key) {
                  case 'practice':
                     return this.xlsxCell(r.practice, bg)
                  case 'hireDate':
                     return this.xlsxCell(r.hireDate && this.isValidDate(r.hireDate) ? dayjs(r.hireDate).format('M/D/YYYY') : (r.hireDate ?? ''), bg)
                  case 'dvmSubmittedOn':
                     return this.isIncompleteSubmitted(r.isDVMSubmitted, r.dvmSubmittedOn)
                        ? this.xlsxCell('INCOMPLETE', bg, true, 'E65100')
                        : this.xlsxCell(r.dvmSubmittedOn, bg)
                  case 'cosSubmittedOn':
                     return this.isIncompleteSubmitted(r.isCOSSubmitted, r.cosSubmittedOn)
                        ? this.xlsxCell('INCOMPLETE', bg, true, 'E65100')
                        : this.xlsxCell(r.cosSubmittedOn, bg)
                  case 'reviewerName':
                     return this.xlsxCell(this.getReviewerName(r) ?? '', bg)
                  case 'status': {
                     const s = this.getReviewStatus(r)
                     return this.xlsxCell(statusLabels[s] ?? s, bg, true, statusFontRgb[s] ?? '1A2B2A')
                  }
                  case 'isDeferred':
                     return r.isDeferred && r.deferralStatus == 'Approved' ? this.xlsxCell('Yes', bg, true, 'B45309') : this.xlsxCell('-', bg)
                  default:
                     return this.xlsxCell((r as any)?.[c.key] ?? '', bg)
               }
            })
         )
      })

      const filename = `performance-hub-${dayjs().format('YYYY-MM-DD')}.xlsx`
      this.xlsxDownload(
         aoa,
         cols.map((c) => colWidths[c.key] ?? 16),
         'Performance Hub',
         filename
      )
      this.closeExportModal()
   }

   private xlsxDownload(aoa: any[][], cols: number[], sheetName: string, filename: string): void {
      import('xlsx-js-style').then((module) => {
         const XLSXStyle = module.default ?? module
         const ws = XLSXStyle.utils.aoa_to_sheet(aoa)

         ws['!cols'] = cols.map((wch) => ({ wch }))
         ws['!rows'] = aoa.map(() => ({ hpt: 21 }))
         ws['!freeze'] = { xSplit: 0, ySplit: 1 }

         const wb = XLSXStyle.utils.book_new()
         XLSXStyle.utils.book_append_sheet(wb, ws, sheetName)
         const buffer = XLSXStyle.write(wb, {
            bookType: 'xlsx',
            type: 'array',
         })
         const url = URL.createObjectURL(
            new Blob([buffer], {
               type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
         )
         const a = document.createElement('a')
         a.href = url
         a.download = filename
         a.click()

         URL.revokeObjectURL(url)
      })
   }

   private xlsxHdr(v: string): any {
      return {
         v,
         s: {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
            fill: { fgColor: { rgb: '005E5D' }, patternType: 'solid' },
            alignment: { vertical: 'center', horizontal: 'left' },
            border: { bottom: { style: 'medium', color: { rgb: '1BB5AE' } } },
         },
      }
   }

   private xlsxCell(v: any, bg: string, bold = false, fontRgb = '1A2B2A'): any {
      return {
         v: v ?? '',
         s: {
            font: { sz: 10, bold, color: { rgb: fontRgb } },
            fill: { fgColor: { rgb: bg }, patternType: 'solid' },
            alignment: { vertical: 'center', wrapText: true },
            border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
         },
      }
   }

   isValidDate(value: string): boolean {
      const date = new Date(value)
      return !isNaN(date.getTime())
   }

   onPracticeChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedPractice = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onReviewerChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedReviewerName = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onRMChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedRMName = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onStatusChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedStatus = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   applyFiltersSort() {
      const byFilters = this.selectedDeferredFilters
      const byName = this.searchName?.toLowerCase()

      const filtered = this.allRows.filter(
         (r) =>
            (byName ? r._nameLower?.includes(byName) : true) &&
            (byFilters.length === 0
               ? true
               : byFilters.some((f) =>
                    f === 'active'
                       ? !this.isDeferredRow(r) && r.revieweeUserStatus != 'LOAINACTIVE'
                       : f === 'deferred'
                         ? this.isDeferredRow(r)
                         : f === 'LOAINACTIVE'
                           ? r.revieweeUserStatus == 'LOAINACTIVE'
                           : true
                 )) &&
            (this.selectedReviewerName ? this.getReviewerName(r) === this.selectedReviewerName : true) &&
            (this.selectedPractice ? r.practice === this.selectedPractice : true) &&
            (this.selectedRMName ? r.rmName === this.selectedRMName : true) &&
            (this.selectedStatus ? this.getReviewStatus(r) === this.selectedStatus : true)
      )

      this.dashboardRows = this.decorate(this.sortArray(filtered, this.currentSort))
      this.buildFilterOptions()
      this.repage()
   }

   // Only this slice ever reaches the DOM.
   private repage(): void {
      this.totalPages = Math.max(1, Math.ceil(this.dashboardRows.length / this.pageSize))
      if (this.pageIndex > this.totalPages - 1) this.pageIndex = this.totalPages - 1
      if (this.pageIndex < 0) this.pageIndex = 0
      const start = this.pageIndex * this.pageSize
      this.pagedRows = this.dashboardRows.slice(start, start + this.pageSize)
   }

   goToPage(i: number): void {
      const next = Math.max(0, Math.min(i, this.totalPages - 1))
      if (next === this.pageIndex) return
      this.pageIndex = next
      this.repage()
      document.querySelector('.table-container')?.scrollTo({ top: 0 })
   }

   get pageRangeStart(): number {
      return this.dashboardRows.length === 0 ? 0 : this.pageIndex * this.pageSize + 1
   }

   get pageRangeEnd(): number {
      return Math.min((this.pageIndex + 1) * this.pageSize, this.dashboardRows.length)
   }

   getReviewStatus(row: DashboardRow): string {
      if (row.isCompleted) return 'completed'
      if (row.dvmSubmittedOn && row.cosSubmittedOn) return 'in-review'
      if (!row.dvmSubmittedOn && !row.cosSubmittedOn && !row.hasStarted) return 'not-started'
      return 'in-progress'
   }

   sortData(sort: Sort) {
      this.currentSort = sort
      this.dashboardRows = this.decorate(this.sortArray(this.dashboardRows, sort))
      this.pageIndex = 0
      this.repage()
   }

   private sortArray(rows: DashboardRow[], sort: Sort): DashboardRow[] {
      if (!sort?.active || !sort.direction) return rows.slice()
      const isAsc = sort.direction === 'asc'

      const getVal = (r: DashboardRow): string | number | null => {
         switch (sort.active) {
            case 'name':
               return r.name?.toLowerCase() ?? ''

            case 'cosName':
               return r.cosName?.toLowerCase() ?? ''

            case 'practice':
               return r.practice?.toLowerCase() ?? ''

            case 'rmName':
               return r.rmName?.toLowerCase() ?? ''

            case 'cosaName':
               return r.vetLeaderName?.toLowerCase() ?? ''

            case 'directorName':
               return r.directorName?.toLowerCase() ?? ''

            case 'vpName':
               return r.vpName?.toLowerCase() ?? ''

            case 'lastUpdated':
               return this.parseDate(r.lastUpdatedDisplay)

            case 'dvmSubmittedOn':
               return this.parseDate(r.dvmSubmittedOn)

            case 'cosSubmittedOn':
               return this.parseDate(r.cosSubmittedOn)

            case 'hireDate':
               return this.parseDate(r.hireDate)

            case 'reviewType':
               return r.reviewType?.toLowerCase() ?? ''

            case 'status':
               return this.getReviewStatus(r)?.toLowerCase() ?? ''

            case 'whenCompletedBy':
               return this.getWhenCompletedBySortValue(r.whenCompletedBy ? r.whenCompletedBy : r.whenOpenCompletedBy)

            default:
               return ''
         }
      }

      // compute each sort key once instead of on every comparison
      // (n log n comparisons means many thousands of calls)
      return rows
         .map((r) => ({ r, v: getVal(r) }))
         .sort((a, b) => {
            const av = a.v
            const bv = b.v
            if (av == null && bv == null) return 0
            if (av == null) return isAsc ? -1 : 1
            if (bv == null) return isAsc ? 1 : -1
            if (av < bv) return isAsc ? -1 : 1
            if (av > bv) return isAsc ? 1 : -1
            return 0
         })
         .map((x) => x.r)
   }

   private getWhenCompletedBySortValue(value?: string | null): number {
      if (!value?.trim()) return 99999999

      const normalized = value.trim().toLowerCase()

      // Put "1 year" after all quarter/year values
      if (normalized === '1 year') {
         return 99999998
      }

      // Example: "Q3 2026"
      const match = normalized.match(/^q([1-4])\s+(\d{4})$/)

      if (match) {
         const quarter = Number(match[1])
         const year = Number(match[2])

         return year * 10 + quarter
      }

      // Any unexpected value goes near the end
      return 99999997
   }

   private parseDate(value: string | Date | null | undefined): number | null {
      if (!value) return null

      if (value instanceof Date) {
         return value.getTime()
      }

      const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)

      if (match) {
         const [, month, day, year] = match

         return new Date(Number(year), Number(month) - 1, Number(day)).getTime()
      }

      const timestamp = Date.parse(value)

      return Number.isNaN(timestamp) ? null : timestamp
   }

   onCycleChange(val: any): void {
      this.selectedCycle = val
      this.pageIndex = 0
      this.refreshCycleState() // the DVM cycle gate depends on selectedCycle
      this.loadDashboard(val)
   }

   // --- Defer ---
   isDeferredRow(row: DashboardRow): boolean {
      return !!row.isDeferred && row.deferralStatus != 'Pending' && row.deferralStatus != 'Rejected' && row.deferralStatus != 'ReEnabled'
   }

   adminReEnableUser(row: DashboardRow): void {
      this.userReviewClient.reEnableDeferredUser(row.revieweeId.toString()).subscribe({
         next: () => {
            row.isDeferred = false
            row.deferReason = ''
            this.applyFiltersSort()
            this.toastr.success('User has been re-enabled.')
         },
         error: () => console.error('Failed to re-enable user'),
      })
   }

   onDeferredFilterChange(val: any): void {
      return
   }

   onDeferredChipChange(event: any): void {
      this.selectedDeferredFilters = event.value ?? []
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   openUserDeferDialog(row: DashboardRow): void {
      this.userDeferDialog.set({ show: true, row })
      this.deferReason = ''
      this.deferQuarter = null
   }

   closeUserDeferDialog(): void {
      this.userDeferDialog.set({ show: false, row: null })
      this.deferReason = ''
      this.deferReasonCategory = ''
      this.deferCategoryOpen = false
      this.deferQuarter = null
   }

   onUserDeferSubmit(): void {
      if (!this.deferReason?.trim() || this.isSubmittingUpdate) return
      const row = this.userDeferDialog().row
      if (!row) return

      const fullReason =
         this.deferReasonCategory === 'Other' || !this.deferReasonCategory
            ? this.deferReason
            : this.deferReason?.trim()
              ? `${this.deferReasonCategory}: ${this.deferReason.trim()}`
              : this.deferReasonCategory

      const finalReviewType = row.reviewType ? row.reviewType : this.reviewType
      const autoApprove = !!this.deferQuarter

      this.isSubmittingUpdate = true
      this.userReviewClient
         .requestDeferral({
            revieweeId: row.revieweeId,
            reviewerUserId: this.getReviewerId(row),
            reviewType: finalReviewType,
            isDeferred: autoApprove,
            deferReason: fullReason,
            practiceSKey: row.practiceSKey,
            practiceName: row.practice,
            revieweeName: row.name,
            cosName: row.cosName,
            rmName: row.rmName,
            vetLeaderName: row.vetLeaderName,
            directorName: row.directorName,
            vpName: row.vpName,
            hireDate: new Date(row.hireDate),
            whenCompletedBy: this.deferQuarter,
            cycle: finalReviewType === 'DVM' ? `aDVM ${this.selectedCycle}` : this.deferQuarter ? 'OPEN' : finalReviewType,
         })
         .subscribe({
            next: () => {
               this.isSubmittingUpdate = false
               if (autoApprove) {
                  row.isDeferred = true
                  row.deferralStatus = 'Approved'
                  row.hasPendingDeferralRequest = false
                  this.closeUserDeferDialog()
                  this.applyFiltersSort()
                  this.toastr.success(`Deferral approved. Review moved to Open Cycle until ${this.deferQuarter}.`)
               } else {
                  row.hasPendingDeferralRequest = true
                  row.deferralStatus = 'Pending'
                  this.closeUserDeferDialog()
                  this.toastr.success('Deferral request has been successfully submitted.')
               }
            },
            error: () => {
               this.isSubmittingUpdate = false
               console.error('Failed to submit deferral request')
            },
         })
   }

   getUserUnreadFollowUpCount(row: DashboardRow): number {
      if (!this.adminFollowUpMap.has(row.revieweeId?.toString())) return 0
      // This is calculated when we load follow-ups in loadAdminFollowUpFlags()
      // Store the count in a separate map
      return this.adminUnreadCountMap.get(row.revieweeId?.toString()) ?? 0
   }

   // User follow-up dialog

   openUserFollowUpDialog(row: DashboardRow): void {
      this.userFollowUpDialog.set({ show: true, row })
      this.userFollowUpMessages = []
      this.userFollowUpMessage = ''
      this.userFollowUpLoading = true

      // Find the deferral request id for this row then load its follow-ups
      this.userReviewClient.getDeferralRequests().subscribe({
         next: (requests) => {
            const req = requests
               .filter((r: any) => r.revieweeId == row.revieweeId && r.status === 'Pending')
               .sort((a: any, b: any) => new Date(b.requestedOn).getTime() - new Date(a.requestedOn).getTime())[0]
            if (!req) {
               this.userFollowUpLoading = false
               return
            }

            this.userFollowUpDeferredOn = req.requestedOn

            this.userReviewClient.getDeferralFollowUps(req.id).subscribe({
               next: (data) => {
                  this.userFollowUpMessages = data.map((d: any) => ({
                     id: d.id,
                     deferralId: d.deferralId,
                     message: d.message,
                     sentById: d.sentById,
                     sentByName: d.sentByName,
                     isAdmin: d.isAdmin,
                     sentOn: d.sentOn,
                  }))
                  this.userFollowUpLoading = false
               },
               error: () => {
                  this.userFollowUpLoading = false
               },
            })
         },
         error: () => {
            this.userFollowUpLoading = false
         },
      })
   }

   closeUserFollowUpDialog(): void {
      this.userFollowUpDialog.set({ show: false, row: null })
      this.userFollowUpMessages = []
      this.userFollowUpMessage = ''
      this.userFollowUpDeferredOn = null
   }

   sendUserFollowUp(): void {
      if (!this.userFollowUpMessage?.trim()) return
      const row = this.userFollowUpDialog().row
      if (!row) return
      this.userFollowUpSaving = true

      // Get the deferral id first then post the reply
      this.userReviewClient.getDeferralRequests().subscribe({
         next: (requests) => {
            const req = requests
               .filter((r: any) => r.revieweeId == row.revieweeId && r.status === 'Pending')
               .sort((a: any, b: any) => new Date(b.requestedOn).getTime() - new Date(a.requestedOn).getTime())[0]
            if (!req) {
               this.userFollowUpSaving = false
               return
            }

            this.userReviewClient
               .addDeferralFollowUp(req.id, {
                  deferralId: req.id,
                  message: this.userFollowUpMessage.trim(),
                  isAdmin: false,
                  revieweeId: req.revieweeId,
               })
               .subscribe({
                  next: () => {
                     this.userFollowUpMessages.push({
                        id: Date.now(),
                        deferralId: req.id,
                        message: this.userFollowUpMessage.trim(),
                        sentById: '',
                        sentByName: this.userName,
                        isAdmin: false,
                        sentOn: new Date(),
                     })
                     this.userFollowUpMessage = ''
                     this.userFollowUpSaving = false
                     // Clear the follow-up badge on this row
                     row.hasAdminFollowUp = false
                  },
                  error: () => {
                     this.userFollowUpSaving = false
                  },
               })
         },
         error: () => {
            this.userFollowUpSaving = false
         },
      })
   }

   openRejectedDialog(row: DashboardRow): void {
      this.rejectedDialog.set({ show: true, row })
      this.rejectedDialogRejectReason = ''
      this.rejectedDialogResolvedBy = ''
      this.rejectedDialogResolvedOn = null

      this.userReviewClient.getDeferralRequests().subscribe({
         next: (requests) => {
            const req = requests
               .filter((r: any) => r.revieweeId == row.revieweeId && r.status === 'Rejected')
               .sort((a: any, b: any) => new Date(b.requestedOn).getTime() - new Date(a.requestedOn).getTime())[0]
            if (!req) return

            this.rejectedDialogRejectReason = req.rejectReason || 'No reason provided.'
            this.rejectedDialogResolvedBy = req.resolvedByName || ''
            this.rejectedDialogResolvedOn = req.resolvedOn
         },
      })
   }

   closeRejectedDialog(): void {
      this.rejectedDialog.set({ show: false, row: null })
      this.rejectedDialogRejectReason = ''
      this.rejectedDialogResolvedBy = ''
      this.rejectedDialogResolvedOn = null
   }

   selectDeferCategory(label: string): void {
      this.deferReasonCategory = label
      this.deferCategoryOpen = false
      this.deferReason = ''
      if (!this.canSelectDeferQuarter) {
         this.deferQuarter = null
      } else if (
         this.deferReasonCategory == this.deferCategoryOptions[0].label ||
         this.deferReasonCategory == this.deferCategoryOptions[1].label ||
         this.deferReasonCategory == this.deferCategoryOptions[5].label ||
         this.deferReasonCategory == this.deferCategoryOptions[6].label
      ) {
         this.deferQuarter = '1 Year'
      } else {
         this.deferQuarter = null
      }
   }

   onNavigateToUserManagement(row: any): void {
      this.router.navigate(['/performance-hub/admin', 'user-management'], {
         state: { pendingUserSelect: row },
      })
   }
}

export interface DashboardRow {
   name: string
   practice: string
   lastUpdated: string
   lastUpdatedDisplay?: string
   hireDate: string
   reviewUpdatedDate?: string
   rmName: string
   vetLeaderName: string
   cosName: string
   directorName: string
   vpName: string
   hmName: string
   hmName2: string
   revieweeId: string
   revieweeUserStatus: string
   isDVMSubmitted?: boolean | null
   isCOSSubmitted?: boolean | null
   dvmSubmittedOn?: string
   cosSubmittedOn?: string
   isCompleted?: boolean | null
   completedOn?: string
   isDeferred?: boolean
   deferReason?: string
   deferralStatus?: string
   practiceSKey?: number
   hasPendingDeferralRequest?: boolean
   hasAdminFollowUp?: boolean
   reviewType: string
   newRM?: string
   newCOS?: string
   newCOSA?: string
   newDO?: string
   newVP?: string
   newHM?: string
   hasStarted?: boolean
   whenCompletedBy?: string
   whenOpenCompletedBy?: string
   hasAttachment?: boolean
}

export interface ViewRow extends DashboardRow {
   _deferred?: boolean
   _onLeave?: boolean
   _unread?: number
   _status?: string
   _nameLower?: string
   _reviewerName?: string
   _newReviewerName?: string
   _dvmIncomplete?: boolean
   _cosIncomplete?: boolean
}
